// components/AdminPanel.js
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Table, Button, Popconfirm, message, Card, InputNumber, Space, Tag, Input, Select, Popover, Checkbox, ConfigProvider, Grid } from 'antd';
import { adminApi } from '../adminApi';
import { KaspiV1Tab, KaspiV2Tab } from './KaspiConsole';

const DEBOUNCE_MS = 400;

// Debounces the search box so we don't fire a request on every keystroke - the DB does the
// filtering/paging now (see AdminSearchSpecifications on the backend), so idle typing shouldn't
// spam it with a query per character.
function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function TransactionsTab() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stoppingId, setStoppingId] = useState(null);
  const [forceClosingId, setForceClosingId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState(undefined);
  const [provider, setProvider] = useState(undefined);
  const [providers, setProviders] = useState([]);
  const search = useDebouncedValue(searchInput, DEBOUNCE_MS);

  useEffect(() => {
    adminApi.get('/api/admin/providers')
      .then((response) => setProviders(response.data))
      .catch(() => {});
  }, []);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/api/admin/transactions/list', {
        params: { page: page - 1, size: pageSize, search: search || undefined, status, provider },
      });
      setTransactions(response.data.content);
      setTotal(response.data.totalElements);
    } catch (error) {
      message.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, status, provider]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Any new search/filter starts back at page 1 - staying on e.g. page 5 of a 2-row result is confusing
  useEffect(() => {
    setPage(1);
  }, [search, status, provider]);

  const handleStop = async (transactionId) => {
    setStoppingId(transactionId);
    try {
      await adminApi.post(`/api/admin/transactions/stop/${transactionId}`);
      message.success(`Transaction ${transactionId} stopped`);
      loadTransactions();
    } catch (error) {
      message.error('Failed to stop transaction');
    } finally {
      setStoppingId(null);
    }
  };

  // For transactions that are stuck "Opened" with no real station response (hung sessions) - marks
  // them Closed directly with no cost calculation, unlike the normal Stop button.
  const handleForceClose = async (transactionId) => {
    setForceClosingId(transactionId);
    try {
      await adminApi.post(`/api/admin/transactions/force-close/${transactionId}`);
      message.success(`Transaction ${transactionId} force-closed (no billing)`);
      loadTransactions();
    } catch (error) {
      message.error('Failed to force-close transaction');
    } finally {
      setForceClosingId(null);
    }
  };

  // Full set of fields the backend returns for a transaction. Not all are shown by default -
  // the operator can pick which extra ones to display via the "Columns" picker below.
  const ALL_COLUMNS = [
    { title: 'ID', dataIndex: 'transactionId', key: 'transactionId', defaultVisible: true },
    // Провайдер, от имени которого создана сессия (transactions.fk_transaction_source); пусто - старые сессии.
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      defaultVisible: true,
      render: (value) => (value ? <Tag color={value === 'KASPI' ? 'red' : 'blue'}>{value}</Tag> : '—'),
    },
    { title: 'External ID', dataIndex: 'externalTransactionId', key: 'externalTransactionId', defaultVisible: false },
    { title: 'Station', dataIndex: 'chargePointSerialNumber', key: 'chargePointSerialNumber', defaultVisible: true },
    { title: 'Connector', dataIndex: 'connectorId', key: 'connectorId', defaultVisible: true },
    { title: 'ID Tag', dataIndex: 'idTag', key: 'idTag', defaultVisible: true },
    { title: 'Start', dataIndex: 'startTime', key: 'startTime', defaultVisible: true },
    { title: 'End', dataIndex: 'endTime', key: 'endTime', defaultVisible: true },
    { title: 'Meter Start', dataIndex: 'meterStart', key: 'meterStart', defaultVisible: false },
    { title: 'Meter Stop', dataIndex: 'meterStop', key: 'meterStop', defaultVisible: false },
    { title: 'kWh', dataIndex: 'kwhConsumed', key: 'kwhConsumed', defaultVisible: true },
    { title: 'Amount', dataIndex: 'transactionAmount', key: 'transactionAmount', defaultVisible: true },
    { title: 'User Phone', dataIndex: 'userPhone', key: 'userPhone', defaultVisible: true },
    {
      title: 'Status',
      dataIndex: 'transactionStatus',
      key: 'transactionStatus',
      defaultVisible: true,
      render: (status) => (
        <Tag color={status === 'Opened' ? 'green' : 'default'}>{status}</Tag>
      ),
    },
    // Lifecycle checkpoints: status1/2 set when the transaction is opened (start-flow checks),
    // status3 set when a stop is requested, status4 set once TransactionBillingService finalizes it.
    { title: 'Status 1', dataIndex: 'transactionStatus1', key: 'transactionStatus1', defaultVisible: false },
    { title: 'Status 2', dataIndex: 'transactionStatus2', key: 'transactionStatus2', defaultVisible: false },
    { title: 'Status 3', dataIndex: 'transactionStatus3', key: 'transactionStatus3', defaultVisible: false },
    { title: 'Status 4', dataIndex: 'transactionStatus4', key: 'transactionStatus4', defaultVisible: false },
    { title: 'Station Location', dataIndex: 'stationLocation', key: 'stationLocation', defaultVisible: false },
    { title: 'Connector Type', dataIndex: 'connectorType', key: 'connectorType', defaultVisible: false },
    { title: 'Connector Status (live)', dataIndex: 'connectorStatus', key: 'connectorStatus', defaultVisible: false },
    { title: 'Updated At', dataIndex: 'timestamp', key: 'timestamp', defaultVisible: false },
    // Always null today - neither field is populated anywhere in the backend (chargePointId has no
    // backing column on Transactions; requestStatus/status only ever get set on start/stop
    // operation responses, not on list rows). Kept toggleable for completeness/consistency.
    { title: 'ChargePoint ID', dataIndex: 'chargePointId', key: 'chargePointId', defaultVisible: false },
    { title: 'Status (raw)', dataIndex: 'status', key: 'status', defaultVisible: false },
    { title: 'Request Status', dataIndex: 'requestStatus', key: 'requestStatus', defaultVisible: false },
  ];

  const [visibleKeys, setVisibleKeys] = useState(
    ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
  );

  const actionColumn = {
    title: 'Action',
    key: 'action',
    render: (_, record) =>
      record.transactionStatus === 'Opened' ? (
        <Space>
          <Popconfirm
            title="Stop this transaction?"
            description="Cost will be calculated and charged immediately."
            onConfirm={() => handleStop(record.transactionId)}
          >
            <Button danger loading={stoppingId === record.transactionId}>
              Stop
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Force-close this transaction?"
            description="No cost will be calculated - use only for stuck/hung transactions."
            onConfirm={() => handleForceClose(record.transactionId)}
          >
            <Button loading={forceClosingId === record.transactionId}>
              Force Close
            </Button>
          </Popconfirm>
        </Space>
      ) : null,
  };

  const columns = [...ALL_COLUMNS.filter((c) => visibleKeys.includes(c.key)), actionColumn];

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by station / phone / idTag"
          allowClear
          style={{ width: 280, maxWidth: '100%' }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 140 }}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'Opened', label: 'Opened' },
            { value: 'Closed', label: 'Closed' },
          ]}
        />
        <Select
          placeholder="Provider"
          allowClear
          style={{ width: 140 }}
          value={provider}
          onChange={setProvider}
          options={providers.map((p) => ({ value: p.code, label: p.code }))}
        />
        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            <Checkbox.Group
              value={visibleKeys}
              onChange={setVisibleKeys}
              options={ALL_COLUMNS.map((c) => ({ label: c.title, value: c.key }))}
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            />
          }
        >
          <Button>Columns</Button>
        </Popover>
      </Space>
      <Table
        rowKey="transactionId"
        columns={columns}
        dataSource={transactions}
        loading={loading}
        scroll={{ x: true }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
      />
    </>
  );
}

function BalancesTab() {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, DEBOUNCE_MS);

  useEffect(() => {
    setLoading(true);
    adminApi
      .get('/api/admin/balances/list', { params: { page: page - 1, size: pageSize, search: search || undefined } })
      .then((response) => {
        setBalances(response.data.content);
        setTotal(response.data.totalElements);
      })
      .catch(() => message.error('Failed to load balances'))
      .finally(() => setLoading(false));
  }, [page, pageSize, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const columns = [
    { title: 'Username', dataIndex: 'username', key: 'username' },
    { title: 'Phone', dataIndex: 'userPhone', key: 'userPhone' },
    { title: 'Balance', dataIndex: 'balance', key: 'balance' },
    { title: 'Purchased', dataIndex: 'purchasedBalance', key: 'purchasedBalance' },
    { title: 'Updated', dataIndex: 'updatedAt', key: 'updatedAt' },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by phone"
          allowClear
          style={{ width: 280, maxWidth: '100%' }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </Space>
      <Table
        rowKey="balanceId"
        columns={columns}
        dataSource={balances}
        loading={loading}
        scroll={{ x: true }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
      />
    </>
  );
}

function WsSessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    adminApi
      .get('/api/admin/ws-sessions/list')
      .then((response) => setSessions(response.data))
      .catch(() => message.error('Failed to load WS sessions'))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { title: 'Station', dataIndex: 'identifier', key: 'identifier' },
    { title: 'Session Index', dataIndex: 'sessionIndex', key: 'sessionIndex' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={status === 'Started' ? 'green' : 'red'}>{status}</Tag>,
    },
    { title: 'ID Tag', dataIndex: 'idTag', key: 'idTag' },
    { title: 'Updated', dataIndex: 'updatedAt', key: 'updatedAt' },
  ];

  return <Table rowKey="sessionIndex" columns={columns} dataSource={sessions} loading={loading} scroll={{ x: true }} />;
}

function RedisStatusTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/api/admin/redis/status');
      setStatus(response.data);
    } catch (error) {
      message.error('Failed to load Redis status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  return (
    <Card
      title="Redis"
      loading={loading}
      style={{ maxWidth: 480, width: '100%' }}
      extra={<Button onClick={loadStatus}>Refresh</Button>}
    >
      {status && (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            Status:{' '}
            <Tag color={status.connected ? 'green' : 'red'}>
              {status.connected ? 'Connected' : 'Unreachable'}
            </Tag>
          </div>
          <div>Host: {status.host}:{status.port}</div>
          {status.connected ? (
            <>
              <div>Used memory: {status.usedMemoryHuman}</div>
              <div>Max memory: {status.maxMemoryHuman}</div>
              <div>Eviction policy: {status.maxMemoryPolicy}</div>
              <div>Keys stored: {status.dbSize}</div>
              <div>Connected clients: {status.connectedClients}</div>
              <div>Uptime: {status.uptimeSeconds != null ? `${Math.floor(status.uptimeSeconds / 3600)}h` : '-'}</div>
              <div>Keyspace hits / misses: {status.keyspaceHits} / {status.keyspaceMisses}</div>
              <div>Evicted keys: {status.evictedKeys}</div>
            </>
          ) : (
            <div style={{ color: 'red' }}>{status.error}</div>
          )}
        </Space>
      )}
    </Card>
  );
}

function TariffTab() {
  const [tariff, setTariff] = useState(null);
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTariff = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/api/admin/tariff');
      setTariff(response.data);
      setRate(response.data.ratePerKwh);
    } catch (error) {
      message.error('Failed to load tariff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTariff();
  }, [loadTariff]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await adminApi.put('/api/admin/tariff', { ratePerKwh: rate });
      setTariff(response.data);
      message.success('Tariff updated');
    } catch (error) {
      message.error('Failed to update tariff');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Current tariff" loading={loading} style={{ maxWidth: 420, width: '100%' }}>
      {tariff && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>Name: {tariff.tariffName}</div>
          <div>Last updated: {tariff.updatedAt}</div>
          <Space>
            <span>Rate per kWh:</span>
            <InputNumber min={0} value={rate} onChange={setRate} />
          </Space>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save
          </Button>
        </Space>
      )}
    </Card>
  );
}

function AdminPanel() {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const mobile = !screens.md;

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const items = [
    { key: 'transactions', label: 'Transactions', children: <TransactionsTab /> },
    { key: 'balances', label: 'Balances', children: <BalancesTab /> },
    { key: 'ws-sessions', label: 'WS Sessions', children: <WsSessionsTab /> },
    { key: 'tariff', label: 'Tariff', children: <TariffTab /> },
    { key: 'redis', label: 'Redis', children: <RedisStatusTab /> },
    { key: 'kaspi', label: 'Kaspi', children: <KaspiV1Tab /> },
    { key: 'kaspi2', label: 'Kaspi v2', children: <KaspiV2Tab /> },
  ];

  // На телефоне - компактные компоненты и отступы; вкладки, не влезающие по ширине, antd прячет в меню «…».
  return (
    <ConfigProvider componentSize={mobile ? 'small' : 'middle'}>
      <div style={{ padding: mobile ? 10 : 20, maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: mobile ? 8 : 16 }}>
          <h2 style={{ margin: 0, fontSize: mobile ? 18 : 24 }}>Admin Panel</h2>
          <Button onClick={handleLogout}>Logout</Button>
        </div>
        <Tabs items={items} size={mobile ? 'small' : 'middle'} />
      </div>
    </ConfigProvider>
  );
}

export default AdminPanel;
